<template>
  <div
    :class="{
      'form-input': true,
      'form-input--required': data.required,
      'form-input--explicit-asterisk': data.required
    }"
    :data-validation="validationName"
  >
    <label class="form-input__label">
      <span class="form-input__caption">
        {{ data.caption }}
        <span v-for="option in selectedOptions" :key="option.id" class="abbreviation-pill">
          {{ option.label }}
        </span>
        <span v-if="showAsterisk" class="form-input__asterisk">*</span>
      </span>
      <FormVisibilityIcon
          v-if="data.visibility"
          class="form-input__visibility"
          v-tooltip="'Field is visible in formlist page'"
          color="#00A800"
        />
    </label>
    <TagAndConfigListInput
      ref="configSelector"
      :customListId="listId"
      :tagGroupData="tagGroupData"
      :timeLineEdit="viewOnly"
      :allowMultipleRoots="false"
      :disableSelectingAnotherParentTag="false"
      :use-abbreviation="data?.use_abbreviation"
      @addNewTag="onAddNewTag"
      @focusTagLevel="onFocusTagLevel"
      @resetSelectedLine="onResetSelectedLine"
    />
  </div>
</template>

<script>
import FormVisibilityIcon from '@/components/common/icons/formVisibilityIcon.vue'
import TagAndConfigListInput from '@/components/common/tagAndConfigListInput.vue'
import { TagTrie } from '@/utils/tagsHelper'
import { mapGetters } from 'vuex'
import { GetCustomList, GetCustomListChild } from '@/api'

export default {
  components: { FormVisibilityIcon, TagAndConfigListInput },
  name: 'configurationList',
  props: {
    mode: {
      type: String,
      default: 'TEMPLATE'
    },
    data: {
      type: Object,
      default: () => {}
    },
    value: {
      type: Array,
      default: () => ([])
    },
    viewOnly: {
      type: Boolean,
      default: false
    },
    returnObjectValue: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      listId: null,
      tagTrie: new TagTrie(),
      tagGroupData: { tagArray: [], firstLevelParents: [] },
      componentValue: [],
      currentValue: [],
      initRequestId: 0
    }
  },
  computed: {
    ...mapGetters('form', ['customListMap']),
    validationName () {
      return `configurationList-${this.data.field_id}`
    },
    showAsterisk () {
      return this.data.required && !String(this.data.caption).includes('*')
    },
    selectedOptions () {
      return (this.tagGroupData?.tagArray || [])
        .map((line, index) => {
          if (!line || !line.length) return null
          const id = line[line.length - 1].id
          const useAbbreviation = this.data?.use_abbreviation
          const label = line.map(tag => (useAbbreviation && tag.abbreviation) ? tag.abbreviation : tag.name).join(' > ')
          return { id: `${id}-${index}`, label }
        })
        .filter(Boolean)
    }
  },
  watch: {
    value (val) {
      this.initFromValue(val)
    },
    data: {
      handler () {
        this.setListId()
      },
      deep: true
    },
    customListMap: {
      handler (newMap) {
        // Re-initialize when the store populates asynchronously after component creation
        if (this.listId && newMap[this.listId]) {
          this.initFromValue(this.value)
        }
      },
      deep: true
    }
  },
  methods: {
    setValue (value) {
      this.initFromValue(value)
    },
    async validateSelection () {
      if (!this.$refs.configSelector?.validateSelection) return true
      return this.$refs.configSelector.validateSelection()
    },
    setListId () {
      this.listId = this.data.custom_list_id || null
    },
    async initFromValue (val) {
      const arr = Array.isArray(val) ? val : []
      const selectedIds = arr
        .map(item => (typeof item === 'object' && item !== null ? item.id : item))
        .filter(id => id !== null && id !== undefined)

      if (this.returnObjectValue) {
        // Retain object structure if it already exists, otherwise use IDs
        this.componentValue = arr.length && typeof arr[0] === 'object' && arr[0] !== null ? arr : selectedIds
        this.currentValue = JSON.parse(JSON.stringify(this.componentValue))
      } else {
        this.componentValue = selectedIds
        this.currentValue = JSON.parse(JSON.stringify(selectedIds))
      }

      const requestId = ++this.initRequestId
      const selectedTags = await this.resolveSelectedTags(arr, selectedIds)
      if (requestId !== this.initRequestId) return
      this.tagTrie = new TagTrie()
      this.tagTrie._generateTreeFromUnorderedList(selectedTags)
      this.tagGroupData = this.tagTrie.groupTagInArray()
    },
    async resolveSelectedTags (rawItems, selectedIds) {
      const list = this.customListMap[this.listId] || []
      const selectedTags = []
      let currentOptions = list

      if (!currentOptions.length && this.listId) {
        const res = await GetCustomList(this.listId)
        currentOptions = res?.core_custom_list_by_pk?.custom_list_values || []
      }

      for (let index = 0; index < selectedIds.length; index++) {
        const id = selectedIds[index]
        const rawItem = rawItems[index]
        let matchedValue = (currentOptions || []).find(item => String(item.id) === String(id))

        if (!matchedValue && rawItem && typeof rawItem === 'object') {
          matchedValue = rawItem
        }

        selectedTags.push({
          id,
          name: matchedValue?.name ?? matchedValue?.value ?? rawItem?.name ?? rawItem?.value ?? '',
          parentId: index ? selectedTags[index - 1].id : null,
          abbreviation: matchedValue?.abbreviation ?? rawItem?.abbreviation ?? null
        })

        if (index < selectedIds.length - 1) {
          const childRes = await GetCustomListChild(id)
          currentOptions = childRes?.custom_list_values || []
        }
      }

      return selectedTags
    },
    onAddNewTag ({ tag }) {
      if (!tag) return
      this.tagTrie.insertTag({
        id: tag.id,
        name: tag.name,
        abbreviation: tag.abbreviation,
        parentId: tag.parentId ?? tag.parent_id ?? null
      })
      this.tagGroupData = this.tagTrie.groupTagInArray()
      this.syncAndEmit()
    },
    onFocusTagLevel ({ index, rowIndex = 0 }) {
      const tagArray = this.tagGroupData?.tagArray || []
      const currentLine = tagArray[rowIndex] || []
      const tagsToRemove = currentLine.slice(index)
      for (let i = tagsToRemove.length - 1; i >= 0; i--) {
        this.tagTrie.deleteTagById2(tagsToRemove[i].id)
      }
      this.tagGroupData = this.tagTrie.groupTagInArray()
      this.syncAndEmit()
    },
    onResetSelectedLine ({ rowIndex = 0 } = {}) {
      const tagArray = this.tagGroupData?.tagArray || []
      const currentLine = tagArray[rowIndex] || []
      if (currentLine.length) {
        this.tagTrie.deleteTagById2(currentLine[0].id)
      }
      this.tagGroupData = this.tagTrie.groupTagInArray()
      this.syncAndEmit()
    },
    syncAndEmit () {
      const tagArray = this.tagGroupData?.tagArray || []
      // Collect all leaf (last) ids from each tag chain — for single-root mode this is row 0
      let newValue
      if (this.returnObjectValue) {
        newValue = (tagArray[0] || []).map(tag => ({
          id: tag.id,
          value: tag.name,
          abbreviation: tag.abbreviation
        }))
      } else {
        newValue = (tagArray[0] || []).map(tag => tag.id)
      }
      this.componentValue = newValue
      let value
      if (JSON.stringify(newValue) !== JSON.stringify(this.currentValue)) {
        value = newValue
      }
      this.$emit('customChange', {
        field_id: this.$props.data.field_id,
        value
      })
    }
  },
  created () {
    this.setListId()
    this.initFromValue(this.value)
  }
}
</script>

<style lang="scss" scoped>
.form-input--explicit-asterisk ::v-deep .form-input__label::after,
.form-input--explicit-asterisk .form-input__label::after {
  content: none !important;
}

.abbreviation-tag {
  font-size: var(--text-xs);
  background: #f4f4f4;
  padding: 1px 5px;
  border-radius: 3px;
  color: #666;
  border: 1px solid #e0e0e0;
  font-weight: 600;
  text-transform: uppercase;
}

.abbreviation-pill {
  background-color: #f8f2e3;
  font-size: var(--text-xs);
  padding: 0 8px;
  color: #6b4e04;
  border-radius: 8px;
}
</style>
